// src/public/PublicReasoningTest.js
import { useState, useEffect } from 'react';
import {
    Box, Container, Paper, TextField, Button, Typography,
    CircularProgress, Alert, InputAdornment
} from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';
import { useParams, useSearchParams } from 'react-router-dom';
import api from '../config/api';
import { useTranslation } from 'react-i18next';
import PublicReasoningTestInstructions from './PublicReasoningTestInstructions';
import PublicReasoningTestQuestions from './PublicReasoningTestQuestions';
import NumericalReasoningExamples from './tests/NumericalReasoningExamples';
import LogicalProcessExamples from './tests/LogicalProcessExamples';
import WordAssociationExamples from './tests/WordAssociationExamples';
import LogicAbstractExamples from './tests/LogicAbstractExamples';
import IntegrityTestExamples from './tests/IntegrityTestExamples';
import EmotionalIntelligenceTestExamples from './tests/EmotionalIntelligenceTestExamples';
import SocialContextTestExamples from './tests/SocialContextTestExamples';

import { DEFAULT_TIME_LIMIT, getTestTimeLimit } from './testTimeLimits';

// Sin timeout, axios espera indefinidamente y el modal de "enviando" gira sin
// fin si el backend se cuelga. Se pasa por llamada y no en la instancia para no
// afectar las rutas lentas de la app (exportes, generacion de documentos).
const COMPLETE_TIMEOUT_MS = 45000;
const COMPLETE_ATTEMPTS = 2;

const PublicReasoningTest = () => {
    const { t, i18n } = useTranslation();
    const { testId, language: urlLanguage } = useParams();
    // Email pre-llenado por query param (?email=...), usado por el portal del
    // candidato para no pedirle de nuevo un dato que ya conocemos de su sesión
    const [searchParams] = useSearchParams();

    const [currentStep, setCurrentStep] = useState('email');
    const [applicantEmail, setApplicantEmail] = useState((searchParams.get('email') || '').trim());
    const [emailError, setEmailError] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [testData, setTestData] = useState(null);
    const [startTime, setStartTime] = useState(null);
    const [timeRemaining, setTimeRemaining] = useState(DEFAULT_TIME_LIMIT * 60);
    const [savedResponses, setSavedResponses] = useState({});
    // Cierre por tiempo agotado y si el envio al backend fallo, solo para
    // decidir que mensaje ve el candidato en la pantalla final.
    const [completionInfo, setCompletionInfo] = useState({ expiredByTime: false, submitFailed: false });
    const [currentQuestionFromBackend, setCurrentQuestionFromBackend] = useState(0);

    // Cambiar idioma si viene en la URL
    useEffect(() => {
        if (urlLanguage && ['es', 'en'].includes(urlLanguage)) {
            i18n.changeLanguage(urlLanguage);
        }
    }, [urlLanguage, i18n]);

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const handleEmailChange = (event) => {
        const value = event.target.value;
        setApplicantEmail(value);
        if (!value.trim()) return setEmailError('');
        setEmailError(isValidEmail(value) ? '' : t('reasoningTests.invalidEmail'));
    };

    const handleAccessValidation = async (e) => {
        e.preventDefault();

        if (!applicantEmail) {
            setEmailError(t('reasoningTests.enterEmail'));
            return;
        }
        if (!isValidEmail(applicantEmail)) {
            setEmailError(t('reasoningTests.invalidEmail'));
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await api.get(
                `/reasoning-tests-public/${testId}`,
                { params: { email: applicantEmail } }
            );

            if (response.data.errorCode === 0 && response.data.response?.data) {
                const data = response.data.response.data;
                setTestData(data);

                if (['completed', 'processing', 'evaluated', 'processing_error'].includes(data.status)) {
                    setCurrentStep('completed');
                } else if (data.status === 'expired') {
                    setCurrentStep('expired');
                } else if (data.status === 'sent' || data.status === 'pending') {
                    setCurrentStep('instructions');
                } else if (data.status === 'started') {
                    // Cargar respuestas guardadas
                    const responses = data.responses || {};
                    setSavedResponses(responses);

                    // Calcular tiempo restante según la duración del tipo de test
                    const startedSeconds = data.startedAt?._seconds ?? data.startedAt?.seconds ?? 0;
                    const startedTime = startedSeconds * 1000;
                    const elapsed = Math.floor((Date.now() - startedTime) / 1000);
                    const remaining = (getTestTimeLimit(data.testType) * 60) - elapsed;

                    setTimeRemaining(remaining > 0 ? remaining : 0);
                    setStartTime(startedTime);

                    // Restaurar pregunta actual
                    setCurrentQuestionFromBackend(data.currentQuestion ?? 0);

                    // Ir al test
                    setCurrentStep('test');
                }
            } else {
                setError(response.data.errorMessage || t('reasoningTests.loadError'));
                setCurrentStep('error');
            }
        } catch (err) {
            console.error('Error validating access:', err);
            if (err.response?.status === 403) {
                setError(t('reasoningTests.accessDenied'));
            } else if (err.response?.status === 404) {
                setError(t('reasoningTests.testNotFound'));
            } else if (err.response?.status === 410) {
                setCurrentStep('expired');
            } else {
                setError(err.response?.data?.errorMessage || t('reasoningTests.loadError'));
            }
            setCurrentStep('error');
        } finally {
            setLoading(false);
        }
    };

    // Nueva función para ir a ejemplos (sin iniciar el test)
    const handleGoToExamples = () => {
        setCurrentStep('examples');
    };

    const handleStartTest = async () => {
        setLoading(true);
        try {
            const response = await api.put(
                `/reasoning-tests-public/${testId}/start`,
                { applicantEmail }
            );

            if (response.data.errorCode === 0) {
                const data = response.data.response.data;
                setTestData(data);
                setStartTime(Date.now());
                setTimeRemaining(getTestTimeLimit(data.testType || testData?.testType) * 60);
                setCurrentStep('test');
            } else {
                setError(response.data.errorMessage);
                setCurrentStep('error');
            }
        } catch (err) {
            console.error('Error starting test:', err);
            setError(err.response?.data?.errorMessage || t('reasoningTests.errorStartingTest'));
            setCurrentStep('error');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Cierra el test en el backend y DEVUELVE si lo logro.
     *
     * Antes no devolvia nada y capturaba su propio error sin relanzarlo, asi que
     * el hijo nunca se enteraba de la falla: su catch no corria, el modal de
     * "no cierres esta ventana" se quedaba girando para siempre y el mensaje de
     * error solo se pintaba en renderError(), que vive en currentStep 'error'
     * y nunca se llegaba a mostrar.
     *
     * `responses` viaja para que el backend pueda recuperar las paginas cuyo
     * guardado individual fallo y solo existian en memoria del navegador.
     */
    const handleCompleteTest = async (responses = null, { expiredByTime = false } = {}) => {
        setLoading(true);

        const payload = { applicantEmail };
        if (responses && Object.keys(responses).length > 0) {
            payload.responses = responses;
        }

        try {
            for (let attempt = 0; attempt < COMPLETE_ATTEMPTS; attempt += 1) {
                try {
                    const response = await api.put(
                        `/reasoning-tests-public/${testId}/complete`,
                        payload,
                        { timeout: COMPLETE_TIMEOUT_MS }
                    );

                    if (response.data.errorCode === 0) {
                        setCompletionInfo({ expiredByTime, submitFailed: false });
                        setCurrentStep('completed');
                        return true;
                    }

                    setError(response.data.errorMessage);
                } catch (err) {
                    // Si el primer intento llego al servidor pero se perdio la
                    // respuesta, el reintento devuelve 400 porque el test ya no
                    // esta en "started". Eso es exito, no error.
                    const currentStatus = err.response?.data?.response?.currentStatus;
                    if (err.response?.status === 400 && currentStatus && currentStatus !== 'started') {
                        setCompletionInfo({ expiredByTime, submitFailed: false });
                        setCurrentStep('completed');
                        return true;
                    }

                    console.error('Error completing test:', err);
                    setError(err.response?.data?.errorMessage || t('reasoningTests.errorCompletingTest'));
                }
            }

            // Se agoto el tiempo y no pudimos registrar: igual hay que sacarlo de
            // las preguntas, porque ya no puede responder. Con el boton manual no,
            // ahi se queda para poder reintentar.
            if (expiredByTime) {
                setCompletionInfo({ expiredByTime: true, submitFailed: true });
                setCurrentStep('completed');
            }

            return false;
        } finally {
            setLoading(false);
        }
    };

    const renderEmailStep = () => (
        <Container maxWidth="sm">
            <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
                <Typography variant="h5" gutterBottom align="center">
                    {t('reasoningTests.emailVerification')}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                    {t('reasoningTests.enterEmailToAccess')}
                </Typography>

                <form onSubmit={handleAccessValidation}>
                    <TextField
                        fullWidth
                        label={t('reasoningTests.enterEmail')}
                        type="email"
                        value={applicantEmail}
                        onChange={handleEmailChange}
                        error={Boolean(emailError)}
                        helperText={emailError}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <EmailIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 3 }}
                    />

                    <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        type="submit"
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : t('reasoningTests.accessTest')}
                    </Button>
                </form>
            </Paper>
        </Container>
    );

    const renderError = () => (
        <Container maxWidth="sm">
            <Alert severity="error" sx={{ mt: 8 }}>
                <Typography variant="h6" gutterBottom>
                    {t('reasoningTests.errorTitle')}
                </Typography>
                <Typography variant="body2">
                    {error || t('reasoningTests.genericError')}
                </Typography>
            </Alert>
        </Container>
    );

    const renderExpired = () => (
        <Container maxWidth="sm">
            <Alert severity="warning" sx={{ mt: 8 }}>
                <Typography variant="h6" gutterBottom>
                    {t('reasoningTests.testExpired')}
                </Typography>
                <Typography variant="body2">
                    {t('reasoningTests.testExpiredMessage')}
                </Typography>
            </Alert>
        </Container>
    );

    const renderCompleted = () => (
        <Container maxWidth="sm">
            {completionInfo.expiredByTime ? (
                <Alert severity="warning" sx={{ mt: 8 }}>
                    <Typography variant="h6" gutterBottom>
                        {t('reasoningTests.timeUpTitle')}
                    </Typography>
                    <Typography variant="body2">
                        {completionInfo.submitFailed
                            ? t('reasoningTests.timeUpSubmitFailedMessage')
                            : t('reasoningTests.timeUpMessage')}
                    </Typography>
                </Alert>
            ) : (
                <Alert severity="success" sx={{ mt: 8 }}>
                    <Typography variant="h6" gutterBottom>
                        {t('reasoningTests.testCompletedTitle')}
                    </Typography>
                    <Typography variant="body2">
                        {t('reasoningTests.testCompletedMessage')}
                    </Typography>
                </Alert>
            )}
        </Container>
    );

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
            {currentStep === 'email' && renderEmailStep()}
            {currentStep === 'error' && renderError()}
            {currentStep === 'expired' && renderExpired()}
            {currentStep === 'instructions' && (
                <PublicReasoningTestInstructions
                    testData={testData}
                    onStart={handleGoToExamples}
                    loading={loading}
                />
            )}
            {currentStep === 'examples' && testData?.testType === 'numericalReasoning' && (
                <NumericalReasoningExamples
                    onStartTest={handleStartTest}
                    loading={loading}
                />
            )}
            {currentStep === 'examples' && testData?.testType === 'logicalProcess' && (
                <LogicalProcessExamples
                    onStartTest={handleStartTest}
                    loading={loading}
                />
            )}
            {currentStep === 'examples' && testData?.testType === 'wordAssociation' && (
                <WordAssociationExamples
                    onStartTest={handleStartTest}
                    loading={loading}
                />
            )}
            {currentStep === 'examples' && testData?.testType === 'logicalAbstract' && (
                <LogicAbstractExamples
                    onStartTest={handleStartTest}
                    loading={loading}
                />
            )}
            {currentStep === 'examples' && testData?.testType === 'integrityTest' && (
                <IntegrityTestExamples
                    onStartTest={handleStartTest}
                    loading={loading}
                />
            )}
            {currentStep === 'examples' && testData?.testType === 'emotionalIntelligence' && (
                <EmotionalIntelligenceTestExamples
                    onStartTest={handleStartTest}
                    loading={loading}
                />
            )}
            {currentStep === 'examples' && testData?.testType === 'socialContext' && (
                <SocialContextTestExamples
                    onStartTest={handleStartTest}
                    loading={loading}
                />
            )}
            {currentStep === 'test' && (
                <PublicReasoningTestQuestions
                    testId={testId}
                    testType={testData.testType}
                    language={testData.language || urlLanguage || 'es'}
                    timeRemaining={timeRemaining}
                    onComplete={handleCompleteTest}
                    initialResponses={savedResponses}
                    initialQuestion={currentQuestionFromBackend}
                    applicantEmail={applicantEmail}
                />
            )}
            {currentStep === 'completed' && renderCompleted()}
        </Box>
    );
};

export default PublicReasoningTest;